/**
 * Core Web App Audit Tracker Provider
 *
 * Wraps the shared AuditTrackerConfigProvider with Core-specific hooks
 * to provide user, corporate, and site data for audit tracking.
 */

import React, { useMemo } from 'react'
import {
  AuditTrackerConfigProvider,
  AuditSource,
  useProductAnalyticsIdentity,
  type UserDataHookResult,
  type CorporateDataHookResult,
  type SiteDataHookResult,
  type ImpersonationDataHookResult,
} from '@hapana/shared/web/hooks'
import useAuthStore from '../stores/auth.store'
import useCorporateStore from '../stores/corporate.store'
import useSiteStore from '../stores/site.store'

/**
 * Hook to provide user data for audit tracking
 * Uses the auth store directly to avoid Router dependency issues
 */
function useCoreUserData(): UserDataHookResult {
  const { user } = useAuthStore()

  const cachedRoles = useMemo(() => {
    try {
      const cached = localStorage.getItem('meQueryCache_v2')
      if (!cached) {
        return []
      }
      const { data, userId } = JSON.parse(cached)
      if (userId !== user?.uid || !data?.me?.type) {
        return []
      }
      return [data.me.type]
    } catch {
      return []
    }
  }, [user?.uid])

  return useMemo(
    () => ({
      // Use Firebase user data - this is available without Router context
      // Note: interface expects 'id' not 'userId'
      id: user?.uid,
      email: user?.email ?? undefined,
      firstName: user?.displayName?.split(' ')?.[0] ?? undefined,
      lastName: user?.displayName ? user.displayName.split(' ').slice(1).join(' ') : undefined,
      roles: cachedRoles,
    }),
    [user, cachedRoles]
  )
}

/**
 * Hook to provide corporate data for audit tracking
 */
function useCoreCorporateData(): CorporateDataHookResult {
  const corporate = useCorporateStore((state) => state.corporate)
  const corporateData = corporate?.corporateBySubdomain

  return useMemo(
    () => ({
      // Note: interface expects 'id' not 'corporateId'
      id: corporateData?.corporateId,
      slug: corporateData?.urlPrefix,
      name: corporateData?.name,
    }),
    [corporateData]
  )
}

/**
 * Hook to provide site data for audit tracking
 */
function useCoreSiteData(): SiteDataHookResult {
  const currentSite = useSiteStore((state) => state.currentSite)

  return useMemo(
    () => ({
      // Note: interface expects 'id' not 'siteId'
      id: currentSite?.siteId,
      name: currentSite?.name,
      slug: currentSite?.slug,
    }),
    [currentSite]
  )
}

/**
 * Hook to provide impersonation data for audit tracking
 * In Core app, check if admin is logged in as a user (loginAs feature)
 */
function useCoreImpersonationData(): ImpersonationDataHookResult {
  // Check for active loginAs session in localStorage
  const activeLoginAsSession =
    typeof window !== 'undefined' ? localStorage.getItem('activeLoginAsSession') : null

  return useMemo(() => {
    if (!activeLoginAsSession) {
      return { isImpersonating: false }
    }

    try {
      const session = JSON.parse(activeLoginAsSession)
      return {
        isImpersonating: true,
        impersonatorUserId: session.adminUserId,
        impersonatorEmail: session.adminEmail,
        impersonatedUserId: session.targetUserId,
      }
    } catch {
      return { isImpersonating: false }
    }
  }, [activeLoginAsSession])
}

interface CoreAuditTrackerProviderProps {
  children: React.ReactNode
}

/**
 * Inner component that syncs identity with product analytics
 * Must be inside AuditTrackerConfigProvider to access the config context
 */
function ProductAnalyticsIdentitySync({ children }: { children: React.ReactNode }) {
  // Sync user/corporate/site context with product analytics (PostHog)
  // This ensures all events have proper context for support and analytics
  useProductAnalyticsIdentity()
  // eslint-disable-next-line react/jsx-no-useless-fragment -- the fragment coerces ReactNode to an element; React 17 throws on an undefined return
  return <>{children}</>
}

/**
 * Core Web App Audit Tracker Provider
 *
 * Provides audit tracking context for the Core web application.
 * Automatically pulls user, corporate, site, and impersonation data
 * from Core's Zustand stores.
 *
 * Also syncs identity with product analytics (PostHog) to ensure all events
 * include corporate_id, corporate_slug, site_id, site_name, user roles,
 * email domain, and device info.
 *
 * Note: Uses auth store directly instead of useMe() to avoid Router context issues.
 */
export function CoreAuditTrackerProvider({ children }: CoreAuditTrackerProviderProps) {
  return (
    <AuditTrackerConfigProvider
      source={AuditSource.CORE_WEB}
      useUserData={useCoreUserData}
      useCorporateData={useCoreCorporateData}
      useSiteData={useCoreSiteData}
      useImpersonationData={useCoreImpersonationData}
    >
      <ProductAnalyticsIdentitySync>{children}</ProductAnalyticsIdentitySync>
    </AuditTrackerConfigProvider>
  )
}

export default CoreAuditTrackerProvider
